/**
 * Node Properties Modal
 * Edit node appearance, behavior, and execution settings.
 * Uses StyleBuilderPanel for color/style and ReactIconPicker for icon selection.
 */

import React, { useState, useEffect, useId, useRef } from 'react';
import { SettingsIcon } from '@bizfirst/common-react';
import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { nodeFormApiClient } from '@flow-studio/api';
import type { NodeFormDto } from '@flow-studio/api';
import { getNodeDisplayProps, ProcessElementConfiguration } from '@flow-studio/types';
import { ModalPortal } from './ModalPortal';
import { ConnectorConfigDialog } from './ConnectorConfigDialog';
import { useDialogFocus, useTopmostEscape } from '../Support/Modal';
import { ReactIconPicker } from '@bizfirst/common-react';
import { StyleBuilderPanel } from '@atlas-forms/designer-components-react';
import type { StyleProperties } from '@atlas-forms/designer-components-react';

interface NodePropertiesModalProps {
  nodeId: string;
  onClose: () => void;
}

export const NodePropertiesModal: React.FC<NodePropertiesModalProps> = ({
  nodeId,
  onClose,
}) => {
  const { nodes, nodeTypes, nodeCategories, updateProcessElement } = useWorkflowStore();
  const { theme } = useUIStore();
  const isDark = theme !== 'light';

  const node = nodes.find((n) => n.id === nodeId);
  const pe = node?.data.processElement;
  const display = node ? getNodeDisplayProps(node.data) : null;

  const [displayName, setDisplayName] = useState(pe?.displayName ?? '');
  const [description, setDescription] = useState(pe?.description ?? '');
  const [icon, setIcon] = useState(display?.icon ?? display?.iconUrl ?? '');
  const [nodeStyle, setNodeStyle] = useState(display?.nodeStyle ?? '');
  const [isDisabled, setIsDisabled] = useState(pe?.isDisabled ?? false);
  const [continueOnFail, setContinueOnFail] = useState(pe?.continueOnFail ?? false);
  const [retryOnFail, setRetryOnFail] = useState(pe?.retryOnFail ?? false);
  const [maxRetries, setMaxRetries] = useState(pe?.maxRetries ?? 0);
  const [timeout, setTimeoutVal] = useState(pe?.timeout ?? 30);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [nodeForms, setNodeForms] = useState<NodeFormDto[]>([]);
  const [formsLoading, setFormsLoading] = useState(false);

  // StyleBuilderPanel state
  const [showStylePanel, setShowStylePanel] = useState(false);
  const [styleAnchorEl, setStyleAnchorEl] = useState<HTMLElement | null>(null);
  const [styleValue, setStyleValue] = useState<StyleProperties>(() => {
    const ui = pe?.configuration?.designer?.ui ?? {};
    return {
      backgroundColor: ui.color,
      ...(ui.style as StyleProperties ?? {}),
    };
  });
  const styleBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!pe?.processElementID) return;
    setFormsLoading(true);
    nodeFormApiClient
      .getNodeForms(pe.processElementID, 'DesignTime')
      .then((response) => {
        if (response.success && response.data) setNodeForms(response.data);
      })
      .catch(() => {})
      .finally(() => setFormsLoading(false));
  }, [pe?.processElementID]);

  // Escape closes this dialog, and only while it is the top-most overlay (a nested config dialog wins).
  // Registered only while the node is shown: the component returns null below otherwise.
  // The ref lets Escape from a control inside this dialog (for example the style panel) reach that
  // control before the dialog closes. See useTopmostEscape.
  const dialogRef = useRef<HTMLDivElement>(null);
  useTopmostEscape(Boolean(node && pe && display), onClose, dialogRef);
  // Focus moves into the dialog on open, Tab stays inside it, and focus returns to the node on close.
  useDialogFocus(dialogRef, Boolean(node && pe && display));
  const titleId = useId();

  if (!node || !pe || !display) return null;

  const handleSave = () => {
    // Merge style back into configuration.designer.ui
    const newConfig: ProcessElementConfiguration = {
      ...pe.configuration,
      designer: {
        ...pe.configuration?.designer,
        ui: {
          ...pe.configuration?.designer?.ui,
          color: styleValue.backgroundColor ?? display.color,
          iconUrl: icon,
          icon,
          nodeStyle,
          style: styleValue as Record<string, any>,
        },
      },
    };

    updateProcessElement(nodeId, {
      displayName,
      description,
      isDisabled,
      continueOnFail,
      retryOnFail,
      maxRetries,
      timeout,
      configuration: newConfig,
    });

    onClose();
  };

  const handleStyleApply = (styles: StyleProperties) => {
    setStyleValue(styles);
    setShowStylePanel(false);
  };

  const themeClass = isDark ? '' : 'light';

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={onClose}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className={`modal modal-wide ${themeClass}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="modal-header-plain">
            <div className="modal-header-content">
              <div className="modal-title" id={titleId}>
                <span>{display.iconUrl}</span>
                <span>Edit Node Properties</span>
              </div>
            </div>
            <button className="modal-close-button" aria-label="Close" onClick={onClose}>×</button>
          </div>

          {/* Content */}
          <div className="modal-body">
            {/* Display Name */}
            <div className="modal-field">
              <label className={`modal-label ${themeClass}`}>Node Name</label>
              <input
                type="text"
                className={`modal-input ${themeClass}`}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter node name"
              />
            </div>

            {/* Description */}
            <div className="modal-field">
              <label className={`modal-label ${themeClass}`}>Description</label>
              <textarea
                className={`modal-textarea ${themeClass}`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            </div>

            {/* Icon — EmojiPicker */}
            <div className="modal-field">
              <label className={`modal-label ${themeClass}`}>Icon</label>
              <ReactIconPicker
                value={icon}
                onChange={(iconName) => setIcon(iconName)}
              />
            </div>

            {/* Node Style — StyleBuilderPanel */}
            <div className="modal-field">
              <label className={`modal-label ${themeClass}`}>Appearance</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Color swatch preview */}
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: styleValue.backgroundColor ?? display.color ?? '#309B48',
                    border: '1px solid var(--border-default)',
                    flexShrink: 0,
                  }}
                />
                <button
                  ref={styleBtnRef}
                  type="button"
                  className={`modal-button secondary ${themeClass}`}
                  style={{ fontSize: 12 }}
                  onClick={() => {
                    setStyleAnchorEl(styleBtnRef.current);
                    setShowStylePanel(true);
                  }}
                >
                  Edit Style…
                </button>
              </div>
            </div>

            {/* Node Style preset */}
            <div className="modal-field">
              <label className={`modal-label ${themeClass}`}>Style Preset</label>
              <input
                type="text"
                className={`modal-input ${themeClass}`}
                value={nodeStyle}
                onChange={(e) => setNodeStyle(e.target.value)}
                placeholder="e.g. filled, outlined, gradient"
              />
            </div>

            {/* Execution Settings */}
            <div className="modal-field">
              <label className={`modal-label ${themeClass}`}>Execution Settings</label>
              <div className="modal-checkbox-container">
                <input
                  type="checkbox"
                  id="isDisabled"
                  className="modal-checkbox"
                  checked={isDisabled}
                  onChange={(e) => setIsDisabled(e.target.checked)}
                />
                <label htmlFor="isDisabled" className={`modal-checkbox-label ${themeClass}`}>
                  Disable node
                </label>
              </div>
              <div className="modal-checkbox-container">
                <input
                  type="checkbox"
                  id="continueOnFail"
                  className="modal-checkbox"
                  checked={continueOnFail}
                  onChange={(e) => setContinueOnFail(e.target.checked)}
                />
                <label htmlFor="continueOnFail" className={`modal-checkbox-label ${themeClass}`}>
                  Continue on fail
                </label>
              </div>
              <div className="modal-checkbox-container">
                <input
                  type="checkbox"
                  id="retryOnFail"
                  className="modal-checkbox"
                  checked={retryOnFail}
                  onChange={(e) => setRetryOnFail(e.target.checked)}
                />
                <label htmlFor="retryOnFail" className={`modal-checkbox-label ${themeClass}`}>
                  Retry on fail
                </label>
              </div>
            </div>

            {retryOnFail && (
              <div className="modal-field">
                <label className={`modal-label ${themeClass}`}>Max Retries</label>
                <input
                  type="number"
                  className={`modal-input ${themeClass}`}
                  value={maxRetries}
                  onChange={(e) => setMaxRetries(parseInt(e.target.value) || 0)}
                  min="0"
                  max="10"
                />
              </div>
            )}

            <div className="modal-field">
              <label className={`modal-label ${themeClass}`}>Timeout (seconds)</label>
              <input
                type="number"
                className={`modal-input ${themeClass}`}
                value={timeout}
                onChange={(e) => setTimeoutVal(parseInt(e.target.value) || 30)}
                min="1"
                max="300"
              />
            </div>

            {/* Atlas Forms */}
            {pe.processElementID && (formsLoading || nodeForms.length > 0) && (
              <div className="modal-field">
                <label className={`modal-label ${themeClass}`}>Configured Forms</label>
                {formsLoading ? (
                  <div className={`modal-info-item ${themeClass}`}>Loading forms…</div>
                ) : (
                  <div className="modal-forms-list">
                    {nodeForms
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((form) => (
                        <div key={form.formID} className={`modal-info-item ${themeClass}`}>
                          <span>{form.formName}</span>
                          {form.source && <span className="modal-badge-source">{form.source}</span>}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* Node Info */}
            <div className={`modal-info-section ${themeClass}`}>
              <div className={`modal-info-item ${themeClass}`}>
                <strong>Node Type:</strong> {display.category}
              </div>
              <div className={`modal-info-item ${themeClass}`}>
                <strong>Node ID:</strong> {nodeId}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={`modal-footer ${themeClass}`}>
            {pe.processElementID && (
              <button
                className={`modal-button secondary ${themeClass}`}
                onClick={() => setIsConfigDialogOpen(true)}
                style={{ marginRight: 'auto' }}
              >
                <SettingsIcon size={14} /> Configure
              </button>
            )}
            <button className={`modal-button secondary ${themeClass}`} onClick={onClose}>
              Cancel
            </button>
            <button className="modal-button primary" onClick={handleSave}>
              Save Changes
            </button>
          </div>
        </div>
      </div>

      {/* StyleBuilderPanel portal */}
      {showStylePanel && styleAnchorEl && (
        <StyleBuilderPanel
          slot="node"
          slotLabel={`${displayName} — Style`}
          value={styleValue}
          anchorEl={styleAnchorEl}
          isDark={isDark}
          onApply={handleStyleApply}
          onClose={() => setShowStylePanel(false)}
        />
      )}

      {/* Connector Config Dialog — opens for any saved node */}
      {pe.processElementID && (
        <ConnectorConfigDialog
          connectorId={display.connectorID}
          processElementId={pe.processElementID}
          isOpen={isConfigDialogOpen}
          onClose={() => setIsConfigDialogOpen(false)}
          onSave={(updatedConnector) => {
            const configuration = typeof updatedConnector.Configuration === 'string'
              ? JSON.parse(updatedConnector.Configuration)
              : (updatedConnector.Configuration ?? {});
            updateProcessElement(node.id, {
              connectorID: updatedConnector.ConnectorID,
              connector: { ...updatedConnector, configuration },
            });
          }}
          initialFormValues={pe?.configuration?.nodeFormValues}
          onFormValuesSave={(values) => {
            const currentConfig = pe?.configuration ?? {};
            // Merge form values onto the configuration ROOT (not just nodeFormValues) —
            // executors (e.g. SqlNodeGuardConfigProvider, BaseHilNodeExecutorSettings)
            // read fields like fieldPolicy/guardRails/engagementConfig/assignedTo directly
            // off the Configuration root, not from a nested nodeFormValues wrapper. Keeping
            // the nodeFormValues copy too preserves this dialog's own restore-on-reopen path
            // and the connector-fallback restore path in ConnectorConfigDialog.
            updateProcessElement(node.id, {
              configuration: {
                ...currentConfig,
                ...values,
                nodeFormValues: { ...currentConfig.nodeFormValues, ...values },
              },
            });
          }}
          acceptedCredentialTypes={(() => {
            const ui = pe?.configuration?.designer?.ui as any;
            const storedDtId = ui?.dataTemplateID as number | undefined;
            const nodeTypeCode = ui?.nodeType as string | undefined;
            const nodeType = storedDtId
              ? (nodeTypes.find(t => t.id === storedDtId) ?? nodeTypes.find(t => t.code === nodeTypeCode))
              : nodeTypes.find(t => t.code === nodeTypeCode);
            return nodeType?.acceptedCredentialTypes
              ?? nodeCategories.find(c => c.code === display.category)?.acceptedCredentialTypes
              ?? (pe?.connector as any)?.configuration?.acceptedCredentialTypes;
          })()}
          nodeCategory={display.category || ''}
          isTrigger={display.isTrigger}
          triggerType={display.triggerType || ''}
          isManual={display.triggerType === 'manual'}
          nodeInfo={{
            id: node.id,
            type: node.type ?? '',
            backendId: pe.processElementID,
            data: {
              displayName: display.displayName,
              description: display.description,
              category: display.category || '',
              connectorId: display.connectorID,
              icon: display.icon,
              headerImageUrl: display.headerImageUrl,
            },
          }}
          onNodeInfoSave={(newDisplayName, newDescription) => {
            updateProcessElement(node.id, { displayName: newDisplayName, description: newDescription });
          }}
        />
      )}
    </ModalPortal>
  );
};
